import { Arg, Args, Ctx, Directive, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { Role, User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, ListParam, getRepository } from '@things-factory/shell'

import { DataArchive } from './data-archive.js'
import { DataArchiveList } from './data-archive-type.js'

var parser = require('cron-parser')

@Resolver(DataArchive)
export class DataArchiveQuery {
  @Directive('@privilege(category: "data-archive", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataArchive, {
    description:
      'Retrieves a single data archive by its unique identifier. This query fetches detailed information about a data archive request including its status, download URL, and associated dataset information for data export purposes.'
  })
  async dataArchive(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<DataArchive> {
    const { domain } = context.state

    return await getRepository(DataArchive).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Directive('@privilege(category: "data-archive", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataArchiveList, {
    description:
      'Retrieves multiple data archives with pagination support. This query is used for listing and managing data export requests, allowing users to track the status of their data download requests and access completed archives.'
  })
  async dataArchives(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataArchiveList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataArchive),
      params,
      domain,
      alias: 'dataArchive',
      searchables: []
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => Domain, {
    description:
      'Retrieves the domain that this data archive belongs to. This field resolver provides access to the organizational context and settings associated with the archive request.'
  })
  async domain(@Root() dataArchive: DataArchive): Promise<Domain> {
    return dataArchive.domainId && (await getRepository(Domain).findOneBy({ id: dataArchive.domainId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who last updated this data archive. This field resolver identifies the person responsible for the most recent modifications to the archive request.'
  })
  async updater(@Root() dataArchive: DataArchive): Promise<User> {
    return dataArchive.updaterId && (await getRepository(User).findOneBy({ id: dataArchive.updaterId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who created this data archive request. This field resolver identifies the person responsible for initiating the data export and download process.'
  })
  async creator(@Root() dataArchive: DataArchive): Promise<User> {
    return dataArchive.creatorId && (await getRepository(User).findOneBy({ id: dataArchive.creatorId }))
  }
}
